import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import BookableItemCombobox, { BookableItemOption } from './BookableItemCombobox';

export type DeeplinkTargetType = 'ITEM' | 'CATEGORY';

export type DeeplinkCategoryOption = {
    id: string;
    nameDe: string;
    nameEn: string;
    isVisible: boolean;
    parent: { nameDe: string } | null;
};

export const bookableItemTypeLabel: Record<'SERVICE' | 'BUNDLE', string> = {
    SERVICE: 'Leistung',
    BUNDLE: 'Bundle',
};

export const formatCategoryName = (category: { nameDe: string; parent?: { nameDe: string } | null }) =>
    category.parent ? `${category.parent.nameDe} / ${category.nameDe}` : category.nameDe;

type DeeplinkRowTarget = {
    bookableItem: { nameDe: string; nameEn: string; type: 'SERVICE' | 'BUNDLE' } | null;
    category: { nameDe: string; nameEn: string; parent: { nameDe: string; nameEn: string } | null } | null;
};

export const getDeeplinkTargetDisplay = (row: DeeplinkRowTarget) => {
    if (row.category) {
        return {
            nameDe: formatCategoryName(row.category),
            nameEn: row.category.parent ? `${row.category.parent.nameEn} / ${row.category.nameEn}` : row.category.nameEn,
            typeLabel: 'Kategorie',
        };
    }
    if (row.bookableItem) {
        return {
            nameDe: row.bookableItem.nameDe,
            nameEn: row.bookableItem.nameEn,
            typeLabel: bookableItemTypeLabel[row.bookableItem.type],
        };
    }
    return { nameDe: '—', nameEn: '', typeLabel: '—' };
};

type DeeplinkTargetFieldsProps = {
    targetType: DeeplinkTargetType;
    onTargetTypeChange: (type: DeeplinkTargetType) => void;
    bookableItems: BookableItemOption[];
    bookableItemId: string;
    onBookableItemIdChange: (id: string) => void;
    categories: DeeplinkCategoryOption[];
    categoryId: string;
    onCategoryIdChange: (id: string) => void;
};

const DeeplinkTargetFields = ({
    targetType,
    onTargetTypeChange,
    bookableItems,
    bookableItemId,
    onBookableItemIdChange,
    categories,
    categoryId,
    onCategoryIdChange,
}: DeeplinkTargetFieldsProps) => {
    return (
        <>
            <div className='space-y-2'>
                <Label>Ziel</Label>
                <Select value={targetType} onValueChange={(v) => onTargetTypeChange(v as DeeplinkTargetType)}>
                    <SelectTrigger>
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value='ITEM'>Buchbare Leistung</SelectItem>
                        <SelectItem value='CATEGORY'>Kategorie</SelectItem>
                    </SelectContent>
                </Select>
                <p className='text-xs text-muted-foreground'>
                    {targetType === 'ITEM'
                        ? 'Öffnet im Buchungstool direkt das Info-Popup der Leistung.'
                        : 'Öffnet im Buchungstool direkt die Kategorie mit allen enthaltenen Leistungen.'}
                </p>
            </div>
            {targetType === 'ITEM' ? (
                <div className='space-y-2'>
                    <Label>Buchbare Leistung</Label>
                    <BookableItemCombobox
                        items={bookableItems}
                        value={bookableItemId || undefined}
                        onValueChange={onBookableItemIdChange}
                        placeholder='Leistung wählen…'
                    />
                </div>
            ) : (
                <div className='space-y-2'>
                    <Label>Kategorie</Label>
                    <Select value={categoryId || undefined} onValueChange={onCategoryIdChange}>
                        <SelectTrigger>
                            <SelectValue placeholder='Kategorie wählen…' />
                        </SelectTrigger>
                        <SelectContent>
                            {categories.map((c) => (
                                <SelectItem key={c.id} value={c.id}>
                                    {formatCategoryName(c)}
                                    {!c.isVisible && <span className='text-muted-foreground'> (ausgeblendet)</span>}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            )}
        </>
    );
};

export default DeeplinkTargetFields;
